import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useShopifyShopLocations from "@babackapp/private-api-client/client/use-shopify-shop-locations";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import { Label } from "@babackapp/ui/components/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { ChevronDown } from "lucide-react";
import { useBoolean } from "@/hooks/use-boolean";

type ExchangeStockLocationSelectProps = {
  value: string | null;
  onChange: (locationId: string | null) => void;
  disabled?: boolean | string;
};

export default function ExchangeStockLocationSelect({
  value,
  onChange,
  disabled,
}: ExchangeStockLocationSelectProps) {
  const t = useTranslation();
  const open = useBoolean(false);
  const { data: shop } = useCurrentShopSuspense();
  const { data: locations, isLoading } = useShopifyShopLocations(shop.id);

  const selectedLocation = locations?.find((loc) => loc.id === value);

  const onSelect = (locationId: string | null) => () => {
    onChange(locationId);
    open.setFalse();
  };

  return (
    <Popover open={open.value} onOpenChange={open.setValue} modal={true}>
      <div>
        <div>
          <Label>
            {t("settings.exchange.fields.stockLocationId.title")}
          </Label>
          <p className="my-0 py-1 max-w-[350px] pb-2 text-sm text-muted-foreground">
            {t("settings.exchange.fields.stockLocationId.description")}
          </p>
        </div>
        <div className="flex justify-start">
          <PopoverTrigger asChild disabled={!!disabled || isLoading}>
            <Button variant="secondary" size="sm" className="mb-6">
              {selectedLocation
                ? selectedLocation.name
                : t("settings.exchange.fields.stockLocationId.globalStock")}
              <ChevronDown className="ml-2 size-4" />
            </Button>
          </PopoverTrigger>
        </div>
      </div>
      <PopoverContent className="p-0">
        <Command
          value={undefined}
          filter={(value, search) => {
            const match = value.toLowerCase().includes(search.toLowerCase());
            return match ? 1 : 0;
          }}
        >
          <CommandInput />
          <CommandList>
            <CommandEmpty>
              {t("carrier.locations.noLocationFound")}
            </CommandEmpty>

            <CommandItem
              className="p-3 font-medium"
              onSelect={onSelect(null)}
              value="-global-stock-"
            >
              {t("settings.exchange.fields.stockLocationId.globalStock")}
            </CommandItem>

            {locations?.map((location) => (
              <CommandItem
                value={location.name}
                className="p-3 font-medium"
                key={location.id}
                onSelect={onSelect(location.id)}
              >
                {location.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
